import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

interface XeroTokenInfo {
  tenantId: string;
  tenantName: string;
  expiryDate: string;
  isValid: boolean;
}

@Component({
  selector: 'app-admin-tools',
  templateUrl: './admin-tools.component.html',
  styleUrls: ['./admin-tools.component.css'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule]
})
export class AdminToolsComponent implements OnInit, OnDestroy {
  tokenInfo: XeroTokenInfo | null = null;
  loadingTokenInfo = false;
  tokenInfoError: string | null = null;

  isAuthorizing = false;
  authorizationComplete = false;

  private authWindow: Window | null = null;
  private checkInterval: any;
  private messageHandler: (e: MessageEvent) => void;

  constructor(private http: HttpClient) {
    this.messageHandler = this.onMessageReceived.bind(this);
  }

  ngOnInit(): void {
    window.addEventListener('message', this.messageHandler);
    this.loadTokenInfo();
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageHandler);
    if (this.checkInterval) clearInterval(this.checkInterval);
    if (this.authWindow && !this.authWindow.closed) this.authWindow.close();
  }

  loadTokenInfo(): void {
    this.loadingTokenInfo = true;
    this.tokenInfoError = null;
    this.http.get<XeroTokenInfo>('/charitycentral/XeroReauthorize/tokeninfo').subscribe({
      next: info => {
        this.tokenInfo = info;
        this.loadingTokenInfo = false;
      },
      error: err => {
        this.tokenInfo = null;
        this.tokenInfoError = err.error?.detail || err.error?.error || 'Unable to load token information.';
        this.loadingTokenInfo = false;
      }
    });
  }

  validateToken(): void {
    this.isAuthorizing = true;
    this.authorizationComplete = false;

    this.http.get<{ authorizationUrl: string }>('/charitycentral/XeroReauthorize/getAuthorizationUrl').subscribe({
      next: response => {
        this.authWindow = window.open(
          response.authorizationUrl,
          'XeroAuth',
          'width=600,height=700,menubar=no,toolbar=no,scrollbars=yes'
        );

        this.checkInterval = setInterval(() => {
          if (this.authWindow?.closed && !this.authorizationComplete) {
            this.isAuthorizing = false;
            clearInterval(this.checkInterval);
          }
        }, 1000);
      },
      error: () => {
        this.isAuthorizing = false;
      }
    });
  }

  cancelAuthorization(): void {
    this.isAuthorizing = false;
    if (this.checkInterval) clearInterval(this.checkInterval);
    if (this.authWindow && !this.authWindow.closed) this.authWindow.close();
  }

  private onMessageReceived(event: MessageEvent): void {
    if (event.origin !== window.location.origin) return;
    if (event.data === 'xero-auth-complete') {
      this.authorizationComplete = true;
      this.isAuthorizing = false;
      if (this.checkInterval) clearInterval(this.checkInterval);
      if (this.authWindow && !this.authWindow.closed) this.authWindow.close();
      this.loadTokenInfo();
    }
  }

  reset(): void {
    this.authorizationComplete = false;
  }

  formatExpiry(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + ' UTC';
  }
}
